import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import SubmissionDetail from '../shared/SubmissionDetail';

const TestDetails = () => {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submissionLoading, setSubmissionLoading] = useState({});
  const [submissionError, setSubmissionError] = useState({});
  const [submissionSuccess, setSubmissionSuccess] = useState({});
  const [showSubmissionForms, setShowSubmissionForms] = useState({});
  const [maxScore, setMaxScore] = useState(7); // Default fallback

  useEffect(() => {
    fetchConfig();
    fetchTestDetails();
  }, [id]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/config`);
      setMaxScore(response.data.maxProblemScore);
    } catch (error) {
      console.warn('Failed to fetch config, using default max score:', error);
      // Keep default fallback value of 7
    }
  };

  const fetchTestDetails = async (silent = false) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tests/${id}`);
      setTest(response.data.test);
      setSubmissions(response.data.submissions);
    } catch (error) {
      if (!silent) {
        setError('Failed to load test details');
        console.error('Error fetching test details:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Poll every 8s to pick up completed AI grading/OCR status — read-only, no AI calls triggered
  useEffect(() => {
    if (!submissions) return;
    const hasProcessing = Object.values(submissions).some(sub =>
      sub &&
      (sub.ocrStatus === 'processing' || sub.ocrStatus === 'pending' ||
       sub.aiGradeStatus === 'processing' || sub.aiGradeStatus === 'pending' ||
       // Text submission: OCR skipped but grading not yet started
       (sub.ocrStatus === 'skipped' && !sub.aiGradeStatus) ||
       // File submission: OCR done but grading not yet started
       (sub.ocrStatus === 'completed' && !sub.aiGradeStatus))
    );
    if (!hasProcessing) return;
    const timer = setInterval(() => fetchTestDetails(true), 8000);
    return () => clearInterval(timer);
  }, [submissions]);

  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState('');
  const [fileViewerName, setFileViewerName] = useState('');
  const [fileViewerType, setFileViewerType] = useState('');

  const handleViewPDF = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/view`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPDFViewer(true);
      } else {
        alert('Failed to load PDF');
      }
    } catch (error) {
      console.error('View error:', error);
      alert('Failed to load PDF');
    }
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  const handleViewFile = async (submissionId, fileIndex, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/download/${fileIndex}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setFileViewerUrl(url);
        setFileViewerName(fileName);
        setFileViewerType(blob.type);
        setShowFileViewer(true);
      } else {
        alert('Failed to load file');
      }
    } catch (error) {
      console.error('File view error:', error);
      alert('Failed to load file');
    }
  };

  const closeFileViewer = () => {
    setShowFileViewer(false);
    if (fileViewerUrl) {
      window.URL.revokeObjectURL(fileViewerUrl);
      setFileViewerUrl('');
      setFileViewerName('');
      setFileViewerType('');
    }
  };

  const handleFileSubmission = async (problemNumber, files, submissionText, closeForm) => {
    const problemKey = `problem-${problemNumber}`;
    setSubmissionLoading({ ...submissionLoading, [problemKey]: true });
    setSubmissionError({ ...submissionError, [problemKey]: '' });
    setSubmissionSuccess({ ...submissionSuccess, [problemKey]: '' });

    try {
      const formData = new FormData();
      formData.append('testId', test._id);
      formData.append('problemNumber', problemNumber);
      formData.append('submissionText', submissionText);
      
      Array.from(files).forEach(file => {
        formData.append('solutionFiles', file);
      });

      const response = await axios.post(`${API_BASE_URL}/api/submissions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSubmissions({
        ...submissions,
        [problemNumber]: response.data,
      });

      setSubmissionSuccess({
        ...submissionSuccess,
        [problemKey]: 'Solution submitted successfully!',
      });

      // Close the form after successful submission
      closeForm();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSubmissionSuccess({ ...submissionSuccess, [problemKey]: '' });
      }, 3000);

    } catch (error) {
      setSubmissionError({
        ...submissionError,
        [problemKey]: error.response?.data?.message || 'Failed to submit solution',
      });
    } finally {
      setSubmissionLoading({ ...submissionLoading, [problemKey]: false });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = (dueDate) => {
    return new Date() > new Date(dueDate);
  };

  const getStatusBadge = (submission) => {
    if (!submission) {
      return <span className="badge badge-secondary">Not Submitted</span>;
    }
    
    switch (submission.status) {
      case 'graded':
        return <span className="badge badge-success">Graded</span>;
      case 'submitted':
        return <span className="badge badge-warning">Submitted</span>;
      default:
        return <span className="badge badge-secondary">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-error">
          {error}
        </div>
        <Link to="/" className="btn btn-secondary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const overdue = isOverdue(test.dueDate);
  const canSubmit = test.status === 'active' && !overdue;

  return (
    <div className="container">
      {/* Test Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0' }}>{test.title}</h1>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span className="badge badge-secondary">Week {test.week}</span>
              {overdue && (
                <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white' }}>
                  Overdue
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleViewPDF}
              className="btn btn-primary"
            >
              View PDF
            </button>
            <button
              onClick={handleDownloadPDF}
              className="btn btn-outline"
            >
              Download PDF
            </button>
            <Link to="/" className="btn btn-secondary">
              Back to Dashboard
            </Link>
          </div>
        </div>
        
        <p style={{ color: '#666', marginBottom: '16px' }}>
          {test.description}
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '14px' }}>
          <div><strong>Problems:</strong> {test.problems.length}</div>
          <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
          <div><strong>Created by:</strong> {test.createdBy.name}</div>
        </div>
      </div>

      {/* Problems List */}
      <div className="card">
        <h2>Problems & Submissions</h2>
        
        {test.problems.map((problem) => (
          <ProblemSubmission
            key={problem.problemNumber}
            problem={problem}
            submission={submissions[problem.problemNumber]}
            canSubmit={canSubmit}
            test={test}
            onSubmit={handleFileSubmission}
            onViewFile={handleViewFile}
            loading={submissionLoading[`problem-${problem.problemNumber}`]}
            error={submissionError[`problem-${problem.problemNumber}`]}
            success={submissionSuccess[`problem-${problem.problemNumber}`]}
            maxScore={maxScore}
          />
        ))}
      </div>

      {/* PDF Viewer Modal */}
      {showPDFViewer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #ddd'
          }}>
            <h3 style={{ margin: 0 }}>{test.title} - PDF Viewer</h3>
            <button
              onClick={closePDFViewer}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, padding: '16px', backgroundColor: '#f5f5f5' }}>
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white'
              }}
              title="PDF Viewer"
            />
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {showFileViewer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #ddd'
          }}>
            <h3 style={{ margin: 0 }}>{fileViewerName} - File Viewer</h3>
            <button
              onClick={closeFileViewer}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, padding: '16px', backgroundColor: '#f5f5f5' }}>
            {fileViewerType.startsWith('image/') ? (
              <img
                src={fileViewerUrl}
                alt={fileViewerName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            ) : fileViewerType === 'application/pdf' ? (
              <iframe
                src={fileViewerUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  backgroundColor: 'white'
                }}
                title="PDF Viewer"
              />
            ) : (
              <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <h3>File Preview Not Available</h3>
                <p>This file type cannot be previewed in the browser.</p>
                <a
                  href={fileViewerUrl}
                  download={fileViewerName}
                  className="btn btn-primary"
                >
                  Download File
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProblemSubmission = ({ problem, submission, canSubmit, test, onSubmit, onViewFile, loading, error, success, maxScore }) => {
  const [files, setFiles] = useState([]);
  const [submissionText, setSubmissionText] = useState(submission?.submissionText || '');
  const [showSubmission, setShowSubmission] = useState(false);

  const handleFileChange = (e) => {
    setFiles(e.target.files);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (files.length === 0 && !submissionText.trim()) {
      alert('Please select files or enter text for your solution');
      return;
    }

    const closeForm = () => {
      setShowSubmission(false);
    };

    onSubmit(problem.problemNumber, files, submissionText, closeForm);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (submission) => {
    if (!submission) {
      return <span className="badge badge-secondary">Not Submitted</span>;
    }
    
    switch (submission.status) {
      case 'graded':
        return <span className="badge badge-success">Graded</span>;
      case 'submitted':
        return <span className="badge badge-warning">Submitted</span>;
      default:
        return <span className="badge badge-secondary">Pending</span>;
    }
  };

  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <h3 style={{ margin: 0 }}>
          Problem {problem.problemNumber}: {problem.title}
        </h3>
        {getStatusBadge(submission)}
      </div>

      {problem.description && (
        <div style={{ color: '#666', marginBottom: '16px' }}>
          <LaTeXRenderer text={problem.description} />
        </div>
      )}

      {/* Show problem source and URL after submission or when the mock is inactive (closed/overdue) */}
      {(!canSubmit || submission) && (problem.source || problem.url) && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #d0e3ff' }}>
          <strong style={{ fontSize: '14px', color: '#333' }}>📚 Problem Source & References:</strong>
          <div style={{ marginTop: '8px', fontSize: '14px' }}>
            {problem.source && (
              <div style={{ marginBottom: '4px' }}><strong>Source:</strong> {problem.source}</div>
            )}
            {problem.url && (
              <div>
                🔗 <a 
                  href={problem.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#007bff', textDecoration: 'underline' }}
                >
                  Problem Link
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submission Status */}
      {submission && (
        <div className="card" style={{ backgroundColor: '#f8f9fa', marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Your Submission</h4>
          
          <div style={{ marginBottom: '12px', fontSize: '14px' }}>
            <div><strong>Submitted:</strong> {formatDate(submission.submittedAt)}</div>
            <div><strong>Status:</strong> {submission.status}</div>
            {submission.score !== null && (
              <div><strong>Score:</strong> {submission.score} / {maxScore}</div>
            )}
          </div>

          <SubmissionDetail
            submission={submission}
            maxScore={maxScore}
            onViewFile={onViewFile}
          />
        </div>
      )}

      {/* Submission Form */}
      {canSubmit && (
        <div>
          <button
            onClick={() => setShowSubmission(!showSubmission)}
            className="btn btn-primary"
            style={{ marginBottom: '16px' }}
          >
            {submission ? 'Update Solution' : 'Submit Solution'}
          </button>

          {showSubmission && (
            <form onSubmit={handleSubmit} className="card" style={{ backgroundColor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 16px 0' }}>
                {submission ? 'Update Your Solution' : 'Submit Your Solution'}
              </h4>

              {error && (
                <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success" style={{ marginBottom: '16px' }}>
                  {success}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Solution Files (Images or PDF)</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleFileChange}
                  className="form-control"
                />
                <div className="text-muted" style={{ fontSize: '14px', marginTop: '4px' }}>
                  You can select multiple image files or PDF files. Max 5MB per file.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Text Solution (Optional)</label>
                <textarea
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  className="form-control"
                  rows="4"
                  placeholder="Enter your solution text here (optional)..."
                />
                {submissionText.trim().length > 0 && submissionText.trim().length < 50 && files.length === 0 && (
                  <div style={{ fontSize: '13px', color: '#856404', marginTop: '4px', padding: '6px 10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    ⚠️ Text is too short, skip AI feedback ({submissionText.trim().length}/50 characters). Add more detail or upload a file to enable AI grading.
                  </div>
                )}
                {submissionText.trim().length >= 50 && files.length === 0 && (
                  <div style={{ fontSize: '13px', color: '#155724', marginTop: '4px' }}>
                    ✓ {submissionText.trim().length} characters — AI feedback will be generated after submission.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >
                  {loading ? (
                    <span>
                      <div className="spinner" style={{ 
                        width: '16px', 
                        height: '16px', 
                        display: 'inline-block', 
                        marginRight: '8px' 
                      }}></div>
                      Submitting...
                    </span>
                  ) : (
                    submission ? 'Update Solution' : 'Submit Solution'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowSubmission(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {!canSubmit && !submission && (
        <div className="alert alert-warning">
          {test.status === 'closed' 
            ? 'Submissions are closed for this test. You can view feedback but cannot submit new solutions.'
            : 'The due date has passed. You can no longer submit solutions for this problem.'
          }
        </div>
      )}
    </div>
  );
};

export default TestDetails;
